// src/pages/Contact.tsx
// Route: /contact. How families reach Future Smart Lab.

import { Link } from "react-router-dom";
import Seo from "../components/Seo";

import { ArrowRight, Mail, MessageCircle } from "lucide-react";
const EMAIL = "hello@futuresmartlab.com";
const WHATSAPP = "https://wa.me/2347052499758";

const FAQ = [
  {
    q: "Which age group is right for my child?",
    a: "Foundation Builders is for ages 6–9, Explorers for 9–12 and Innovators for 13–18. A 9-year-old can join either; tell us what they enjoy and we'll suggest one.",
  },
  {
    q: "Are the classes live?",
    a: "No. Lessons are short videos your child watches when it suits your family. They do the activity, upload what they made, and a tutor reviews every piece with written or voice feedback.",
  },
  {
    q: "Is there an on-site option?",
    a: "Online is open everywhere now. Hybrid and on-site open area by area once enough families sign up nearby. Register your interest when you enrol and we'll email you when your area opens.",
  },
  {
    q: "What does the hardware kit include?",
    a: "Each age group gets its own kit once a year, posted to you after your first payment for online or hybrid learning. You'll find the full list on My Kit after you enrol.",
  },
];

export default function ContactPage() {
  return (
    <div className="bg-paper">
      <Seo
        title="Contact us"
        description="Talk to Future Smart Lab by email or WhatsApp about enrolment, lessons, kits and on-site learning."
        path="/contact"
      />
      <section className="mx-auto max-w-5xl px-4 pb-6 pt-14 sm:pt-20">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-500">Contact</p>
        <h1 className="mt-3 max-w-2xl text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
          We're happy to help your family get started
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-7 text-ink-mute sm:text-lg">
          Questions about age groups, prices, kits or how the lessons work? Message us. We usually reply within one working day.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noreferrer"
            className="group rounded-[26px] bg-brand-600 p-6 text-white shadow-card transition hover:-translate-y-0.5"
          >
            <MessageCircle className="h-8 w-8 flex-shrink-0" aria-hidden />
            <p className="mt-4 text-lg font-bold">WhatsApp</p>
            <p className="mt-1 text-sm leading-6 text-white/75">The quickest way to reach us.</p>
            <p className="mt-4 font-semibold text-gold-300">+234 705 249 9758{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></p>
          </a>
          <a
            href={`mailto:${EMAIL}`}
            className="group rounded-[26px] border border-[#e5e8ef] bg-white p-6 shadow-soft transition hover:-translate-y-0.5 hover:border-brand-300"
          >
            <Mail className="h-8 w-8 flex-shrink-0" aria-hidden />
            <p className="mt-4 text-lg font-bold text-ink">Email</p>
            <p className="mt-1 text-sm leading-6 text-ink-mute">For anything detailed, or to send documents.</p>
            <p className="mt-4 font-semibold text-brand-600">{EMAIL}{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></p>
          </a>
        </div>
      </section>

      <section id="faq" className="mx-auto max-w-5xl scroll-mt-24 px-4 py-10">
        <h2 className="text-2xl font-extrabold tracking-tight text-ink">Common questions</h2>
        <div className="mt-5 divide-y divide-[#e5e8ef] rounded-[26px] border border-[#e5e8ef] bg-white">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-bold text-ink">
                {f.q}
                <span className="text-xl text-brand-400 transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-6 text-ink-mute">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-20 pt-4">
        <div className="flex flex-col items-start justify-between gap-5 rounded-[26px] border border-gold-300 bg-gold-100 p-6 sm:flex-row sm:items-center sm:p-8">
          <div>
            <p className="text-xl font-extrabold text-ink">Ready to begin?</p>
            <p className="mt-1 text-sm text-ink-soft">Enrolment takes about five minutes. Your child can start this week.</p>
          </div>
          <Link to="/enroll" className="rounded-2xl bg-brand-600 px-6 py-3 text-[15px] font-bold text-white transition hover:bg-brand-700">
            Enrol a child
          </Link>
        </div>
      </section>
    </div>
  );
}
